import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TRIWULAN_OPTIONS, triwulanLabel } from "../lib/constants";
import { bulkTriwulan } from "../lib/api";

export const BulkTriwulanModal = ({ open, jobs, onClose, onChanged }) => {
  const [selected, setSelected] = useState({});
  const [triwulan, setTriwulan] = useState("");
  const [tahun, setTahun] = useState("");
  const [saving, setSaving] = useState(false);
  const [onlyEmpty, setOnlyEmpty] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setTriwulan("");
    setTahun(String(new Date().getFullYear()));
    setOnlyEmpty(true);
  }, [open]);

  if (!open) return null;

  const list = onlyEmpty ? jobs.filter((j) => !j.triwulan) : jobs;
  const ids = list.map((j) => j.id);
  const selectedIds = ids.filter((id) => selected[id]);
  const allChecked = ids.length > 0 && selectedIds.length === ids.length;

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(ids.map((id) => [id, true])));
  };

  const submit = async () => {
    if (selectedIds.length === 0) return toast.error("Pilih minimal satu pekerjaan");
    if (!triwulan) return toast.error("Pilih triwulan");
    setSaving(true);
    try {
      const res = await bulkTriwulan(selectedIds, Number(triwulan), tahun ? Number(tahun) : null);
      toast.success(`${res.updated} pekerjaan diperbarui`);
      onChanged && onChanged();
      onClose();
    } catch {
      toast.error("Gagal memperbarui");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none bg-white font-semibold";

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-auto flex flex-col max-h-[92vh] animate-fade-up" data-testid="bulk-triwulan-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-teal-800 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2"><i className="ph-bold ph-calendar-plus text-teal-300" /> Isi Massal Triwulan</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10" data-testid="bulk-triwulan-close"><i className="ph-bold ph-x text-xl" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Triwulan <span className="text-red-500">*</span></label>
              <select value={triwulan} onChange={(e) => setTriwulan(e.target.value)} className={inputCls} data-testid="bulk-triwulan-select">
                <option value="">— Pilih —</option>
                {TRIWULAN_OPTIONS.map((t) => <option key={t} value={t}>Triwulan {t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tahun</label>
              <input type="number" value={tahun} onChange={(e) => setTahun(e.target.value)} placeholder="2026" min="2000" max="2100" className={`${inputCls} w-28`} data-testid="bulk-triwulan-tahun" />
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 ml-auto cursor-pointer">
              <input type="checkbox" checked={onlyEmpty} onChange={(e) => { setOnlyEmpty(e.target.checked); setSelected({}); }} className="w-4 h-4 accent-teal-600" data-testid="bulk-only-empty" />
              Hanya yang belum punya triwulan
            </label>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{list.length} Pekerjaan • {selectedIds.length} dipilih</span>
            {list.length > 0 && (
              <button onClick={toggleAll} className="text-xs font-bold text-teal-700 hover:text-teal-900" data-testid="bulk-toggle-all">
                {allChecked ? "Batal pilih semua" : "Pilih semua"}
              </button>
            )}
          </div>

          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
            {list.length === 0 && <div className="p-6 text-center text-slate-400 text-xs">Tidak ada pekerjaan.</div>}
            {list.map((j) => (
              <label key={j.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`bulk-job-${j.id}`}>
                <input type="checkbox" checked={!!selected[j.id]} onChange={() => toggle(j.id)} className="w-4 h-4 accent-teal-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800 truncate">{j.title}</p>
                  <p className="text-[11px] text-slate-400 font-semibold">{j.location_name} • {triwulanLabel(j)}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-50">Batal</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg shadow-sm disabled:opacity-60" data-testid="bulk-triwulan-submit">
            {saving ? "Menyimpan..." : `Terapkan ke ${selectedIds.length} pekerjaan`}
          </button>
        </div>
      </div>
    </div>
  );
};
